import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  SortableTableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { ComparisonRow, RegistrationComparison } from '@/lib/registration-comparison'
import { RegistrationChange } from './registration-comparison-figures'
import { EmptyState } from '@/components/ui/empty-state'
import type { RegistrationQueryControls } from '@/lib/registration-comparison-query'
import { useState } from 'react'
import { RegistrationPeopleSheet } from './registration-people-sheet'

type Column = {
  key:
    | 'places'
    | 'unnamed'
    | 'same_point'
    | 'last_final'
    | 'change'
    | 'checked_last'
    | 'checked_now'
  label: string
}

function columnsFor(report: RegistrationComparison): Column[] {
  const final = report.mode === 'final'
  const base: Column[] = [
    { key: 'places', label: final ? 'Places' : 'Places now' },
    { key: 'unnamed', label: 'Awaiting names' },
  ]
  const currentCheckIns: Column[] = final
    ? [{ key: 'checked_now', label: 'Checked in this year' }]
    : []
  if (report.previous_edition !== 'ok') return [...base, ...currentCheckIns]
  return [
    ...base,
    {
      key: final ? 'last_final' : 'same_point',
      label: final ? 'Last year final' : 'Same point last year',
    } as Column,
    { key: 'change', label: 'Change' } as Column,
    ...(!final ? [{ key: 'last_final', label: 'Last year final' } as Column] : []),
    { key: 'checked_last', label: 'Checked in last year' } as Column,
    ...currentCheckIns,
  ]
}

export function RegistrationTable({
  report,
  params,
  update,
}: { report: RegistrationComparison } & RegistrationQueryControls) {
  const [selected, setSelected] = useState<{
    row: ComparisonRow
    trigger: HTMLElement
    eventId: string
  } | null>(null)
  const columns = columnsFor(report)
  const [key, order] = (params.get('sort') || 'places:desc').split(':')
  const sortProps = (column: string) => ({
    active: key === column,
    direction: order === 'asc' ? ('ascending' as const) : ('descending' as const),
    initialDirection: column === 'name' ? ('ascending' as const) : ('descending' as const),
    onToggle: () => {
      const next =
        key === column ? (order === 'asc' ? 'desc' : 'asc') : column === 'name' ? 'asc' : 'desc'
      update('sort', `${column}:${next}`)
    },
  })
  return (
    <div className="mt-3 grid min-w-0 grid-cols-1">
      <Table
        className={cn(
          'table-fixed',
          report.previous_edition === 'ok' ? 'min-w-[48rem]' : 'min-w-[28rem] max-w-xl',
        )}
      >
        <TableHeader>
          <TableRow>
            <SortableTableHead
              {...sortProps('name')}
              className={cn(
                'whitespace-normal py-3',
                report.previous_edition === 'ok' ? 'w-[28%]' : 'w-[40%]',
              )}
            >
              Organization
            </SortableTableHead>
            {columns.map((column) => (
              <SortableTableHead
                {...sortProps(column.key)}
                key={column.key}
                title={
                  column.key === 'unnamed'
                    ? 'Booked places with no attendee name assigned yet'
                    : undefined
                }
                className="whitespace-normal px-2 py-3 text-right [&_button]:w-full [&_button]:justify-end [&_button]:text-right [&_svg]:shrink-0"
              >
                {column.label}
              </SortableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <RegistrationRow row={report.totals} columns={columns} total />
          {report.rows.map((row) => (
            <RegistrationRow
              key={row.id ?? row.name}
              row={row}
              columns={columns}
              onOpen={
                row.id || row.unassigned
                  ? (trigger) => setSelected({ row, trigger, eventId: report.event.id })
                  : undefined
              }
            />
          ))}
        </TableBody>
      </Table>
      {report.rows.length === 0 && <EmptyState variant="quiet" title="No organizations match" />}
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-caption text-muted-foreground">{report.organization_count_label}</p>
        <nav aria-label="Organization pages" className="flex gap-2">
          <Button
            variant="outline"
            disabled={report.pagination.previous_cursor === null}
            onClick={() => update('cursor', report.pagination.previous_cursor ?? '')}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            disabled={report.pagination.next_cursor === null}
            onClick={() => update('cursor', report.pagination.next_cursor ?? '')}
          >
            Next
          </Button>
        </nav>
      </div>
      {selected?.eventId === report.event.id && (
        <RegistrationPeopleSheet
          key={`${selected.eventId}:${selected.row.id ?? 'none'}`}
          eventId={selected.eventId}
          row={selected.row}
          trigger={selected.trigger}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

function RegistrationRow({
  row,
  columns,
  total,
  onOpen,
}: {
  row: ComparisonRow
  columns: Column[]
  total?: boolean
  onOpen?: (trigger: HTMLElement) => void
}) {
  return (
    <TableRow
      className={cn(
        'h-9',
        total && 'bg-muted/40 font-medium hover:bg-muted/40',
        onOpen && 'cursor-pointer',
      )}
      tabIndex={onOpen ? 0 : undefined}
      onClick={(event) => {
        if (
          !onOpen ||
          event.button !== 0 ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          event.altKey
        )
          return
        event.preventDefault()
        onOpen(event.currentTarget)
      }}
      onKeyDown={(event) => {
        if (onOpen && event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
          event.preventDefault()
          onOpen(event.currentTarget)
        }
      }}
    >
      <TableCell className="whitespace-normal py-2">
        <span className={cn('inline-flex items-center gap-2', !row.id && 'text-muted-foreground')}>
          {row.id && !total ? (
            <Button
              variant="link"
              size="inline"
              asChild
              className="h-auto whitespace-normal break-words text-left font-normal text-foreground hover:text-foreground"
            >
              <a href={`/contacts/organizations/${encodeURIComponent(row.id)}`}>{row.name}</a>
            </Button>
          ) : (
            row.name
          )}
          {row.archived && <Badge variant="neutral">Archived</Badge>}
        </span>
      </TableCell>
      {columns.map((column) => (
        <TableCell
          key={column.key}
          className={cn(
            'py-0 text-right tabular-nums',
            column.key === 'unnamed' && 'font-normal text-muted-foreground',
          )}
        >
          {column.key === 'change' ? <RegistrationChange change={row.change} /> : row[column.key]}
        </TableCell>
      ))}
    </TableRow>
  )
}
